import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import UploadIcon from "@redotech/redo-icons/upload-01_filled.svg";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import {
  DiscountMatchType,
  DiscountOptionModel,
} from "@redotech/redo-return-core-common/return-flow/condition-enums";
import type { DiscountState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { UploadCsvModal } from "./upload-csv-modal";

function matchTypeOptions(
  t: (key: string) => string,
): RadioGroupOption<DiscountMatchType>[] {
  return [
    { id: DiscountMatchType.EXACT, label: t("Exact match") },
    { id: DiscountMatchType.CONTAINS, label: t("Contains") },
  ];
}

function codeOptionOptions(
  t: (key: string) => string,
): RadioGroupOption<DiscountOptionModel>[] {
  return [
    { id: DiscountOptionModel.ORDER, label: t("All items in order") },
    {
      id: DiscountOptionModel.ITEM,
      label: t("Only items with discount code applied"),
    },
  ];
}

export const DiscountDetails = memo(function DiscountDetails({
  state,
  setState,
  disabled,
  flowType,
}: ConditionTypeDetailsProps<DiscountState>) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const stateRef = useRef(state);
  const setStateRef = useRef(setState);

  // Keep refs updated
  useEffect(() => {
    stateRef.current = state;
    setStateRef.current = setState;
  });

  useEffect(() => {
    if (
      flowType === FlowType.FINALIZE_RETURN ||
      flowType === FlowType.FINALIZE_CLAIM
    ) {
      setState({ ...state, codeOption: DiscountOptionModel.ORDER });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenUploadModal = useHandler(() => {
    setUploadModalOpen(true);
  });

  const handleCloseUploadModal = useHandler(() => {
    setUploadModalOpen(false);
  });

  const handleValuesUploaded = useHandler((newCodes: string[]) => {
    const currentState = stateRef.current;
    const existingCodes = currentState.codes || [];
    const uniqueCodes = [...new Set([...existingCodes, ...newCodes])];
    setStateRef.current({ ...currentState, codes: uniqueCodes });
  });

  const tags = useMemo(
    () => state.codes.map((code) => ({ text: code, color: "gray" as const })),
    [state.codes],
  );

  return (
    <>
      <RedoRadioGroup
        disabled={disabled}
        options={matchTypeOptions(t)}
        setValue={(matchType) => setState({ ...state, matchType })}
        size="xs"
        value={state.matchType}
      />
      <RedoTagInputField
        delimiters={["newline"]}
        description={t("List of discount codes or titles, case-insensitive")}
        inputValue={inputValue}
        label={t("Discount codes/titles")}
        placeholder={t("Type and press Enter to add")}
        setInputValue={setInputValue}
        setTags={(codes) => setState({ ...state, codes })}
        size="sm"
        state={disabled ? "disabled" : "default"}
        tags={tags}
      />
      <Flex dir="column" gap="sm">
        <RedoButton
          disabled={disabled}
          hierarchy="secondary"
          IconLeading={UploadIcon}
          onClick={handleOpenUploadModal}
          size="sm"
          type="button"
        >
          {t("Upload CSV")}
        </RedoButton>
      </Flex>
      {flowType !== FlowType.FINALIZE_RETURN &&
        flowType !== FlowType.FINALIZE_CLAIM && (
          <RedoRadioGroup
            disabled={disabled}
            label={t("Condition is applied to")}
            options={codeOptionOptions(t)}
            setValue={(codeOption) => setState({ ...state, codeOption })}
            size="xs"
            value={state.codeOption}
          />
        )}
      {uploadModalOpen && (
        <UploadCsvModal
          onClose={handleCloseUploadModal}
          onValuesUploaded={handleValuesUploaded}
          open={uploadModalOpen}
          possibleColumnNames={[
            "discount_code",
            "code",
            "discount",
            "title",
            "discount_title",
          ]}
          valueLabel={t("discount codes")}
        />
      )}
    </>
  );
});
